import { useState, useEffect, useRef } from 'react';

/**
 * useNetworkStatus — "can we reach the server right now?"
 *
 * Replaces src/hooks/useNetworkStatus.js, which had four problems that
 * made it unusable for a cache:
 *
 *   1. It fired toasts itself. A hook that renders UI can only be
 *      mounted once; mount it in two places and every drop produces two
 *      stacked error toasts. This one returns state and nothing else —
 *      whoever wants a toast can write one.
 *   2. Its effect depended on [isOnline, wasOffline], so every status
 *      change tore down and rebuilt the 30s interval, and the listeners
 *      it registered closed over stale state.
 *   3. It polled https://www.google.com every 30 seconds forever, from
 *      a school intranet, to answer a question about OUR server. A
 *      no-cors HEAD to Google also resolves as an opaque response even
 *      when it fails in ways that matter, so it reported "online" while
 *      the API was unreachable.
 *   4. Polling at all. `navigator.onLine` plus the actual outcome of
 *      the requests the app is already making is strictly better
 *      information and costs nothing.
 *
 * The model here has two inputs:
 *
 *   navigator.onLine + the online/offline events
 *       Cheap and instant, but it only means "this machine has a
 *       network interface with a route". A laptop connected to a wifi
 *       captive portal reports true. So it is treated as authoritative
 *       for OFFLINE (false really does mean no requests can succeed)
 *       and merely optimistic for ONLINE.
 *
 *   reportFailure() / reportSuccess()
 *       Called by useCachedData from the real fetches. A fetch that
 *       throws a network error (not an HTTP error — the API client
 *       distinguishes them) is the strongest possible evidence that we
 *       are offline, and a fetch that succeeds is proof that we are
 *       not. This is how the browser reports "offline" while the page
 *       still works, and "online" on a portal that eats every request.
 *
 * The state is module-level, not per-hook, so every consumer agrees and
 * a failure observed by one page immediately updates the navbar.
 */

// --- module-level singleton state ------------------------------------

let online = typeof navigator === 'undefined' ? true : navigator.onLine !== false;

/** Set when a real request failed with a network error while
 *  navigator.onLine was still true (captive portal / server down). */
let probeFailed = false;

const listeners = new Set();

function publish() {
  listeners.forEach((fn) => { try { fn(); } catch { /* one bad listener must not stop the rest */ } });
}

function setOnline(next) {
  const value = !!next;
  if (value === online && !probeFailed) return;
  online = value;
  if (value) probeFailed = false;
  publish();
}

/**
 * Called after a fetch fails with a *network* error. ui/api.js throws
 * 'تعذّر الاتصال بالخادم' for those and an Error with `.status` for HTTP
 * failures, so callers can tell them apart — a 403 must NOT mark the app
 * offline.
 */
export function reportNetworkFailure() {
  if (!online && probeFailed) return;
  online = false;
  probeFailed = true;
  publish();
}

/** Called after any successful fetch. Proof of connectivity. */
export function reportNetworkSuccess() {
  if (online && !probeFailed) return;
  online = true;
  probeFailed = false;
  publish();
}

/** Current value, for non-React callers. */
export function isOnline() {
  return online;
}

// Browser events are registered once for the whole module rather than
// per-hook-instance: twenty mounted pages must not mean twenty
// listeners. Guarded for SSR/tests where `window` is absent.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    // The OS says an interface came back. Believe it optimistically and
    // clear the probe flag: the next real request will correct us
    // within one page load if it was a captive portal.
    probeFailed = false;
    setOnline(true);
  });
  window.addEventListener('offline', () => setOnline(false));
}

// --- the hook ---------------------------------------------------------

/**
 * @returns {{
 *   online: boolean,        // best current guess at reachability
 *   offline: boolean,       // convenience inverse, reads better in JSX
 *   since: number|null,     // Date.now() when we went offline, else null
 *   wasOffline: boolean     // true once a drop has happened this session
 * }}
 *
 * Usage:
 *   const { offline } = useNetworkStatus();
 *   {offline && <span>غير متصل — تُعرض بيانات مخزّنة</span>}
 */
export default function useNetworkStatus() {
  const [value, setValue] = useState(online);
  const [since, setSince] = useState(online ? null : Date.now());
  const [wasOffline, setWasOffline] = useState(!online);

  // Read through a ref inside the subscription so the effect can depend
  // on [] and register exactly once for the life of the component. The
  // old hook's [isOnline, wasOffline] dependency array is what made it
  // re-subscribe on every change.
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    const sync = () => {
      const next = online;
      if (next === valueRef.current) return;
      valueRef.current = next;
      setValue(next);
      if (next) {
        setSince(null);
      } else {
        setSince(Date.now());
        setWasOffline(true);
      }
    };

    listeners.add(sync);
    // The module value may have changed between the initial useState and
    // this effect running (a failed request during first paint).
    sync();
    return () => { listeners.delete(sync); };
  }, []);

  return { online: value, offline: !value, since, wasOffline };
}

/**
 * Subscribe from non-React code. Returns an unsubscribe function.
 * Used by CacheToggle's status dot so the navbar reflects connectivity
 * without the toggle needing to own the hook.
 */
export function subscribeNetwork(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** True when an error thrown by ui/api.js request() was a transport
 *  failure rather than an HTTP/application error. The client wraps
 *  transport failures with `cause` set and no `status`. */
export function isNetworkError(err) {
  if (!err) return false;
  if (typeof err.status === 'number') return false; // a real HTTP response came back
  return true;
}

/** Tiny helper kept here so both the hook and useCachedData agree on
 *  the classification in one place. */
export const network = {
  isOnline,
  reportNetworkFailure,
  reportNetworkSuccess,
  subscribeNetwork,
  isNetworkError,
};
